import {scaleLinear} from 'd3-scale';
import {area, curveMonotoneX, line} from 'd3-shape';
import {interpolate} from 'remotion';
import type {Peak, WeeklyDatum} from '../data';
import {fontFamily, theme} from '../theme';

const WIDTH = 1728;
const HEIGHT = 234;
const PLOT_LEFT = 112;
const PLOT_RIGHT = 34;
const PLOT_TOP = 42;
const PLOT_BOTTOM = 28;

type MetricKey = 'sessions' | 'minutes';

type TrendChartProps = {
  rows: WeeklyDatum[];
  metric: MetricKey;
  label: string;
  unit: string;
  color: string;
  maxValue: number;
  peak: Peak;
  dataProgress: number;
  domainStart: number;
  domainEnd: number;
  frame: number;
  outroProgress: number;
  fillArea?: boolean;
};

type PlotPoint = {index: number; value: number};

const tickFormatter = new Intl.NumberFormat('zh-CN');
const formatTick = (value: number) => tickFormatter.format(Math.round(value));

export const TrendChart = ({
  rows,
  metric,
  label,
  unit,
  color,
  maxValue,
  peak,
  dataProgress,
  domainStart,
  domainEnd,
  frame,
  outroProgress,
  fillArea = false,
}: TrendChartProps) => {
  const x = scaleLinear().domain([domainStart, domainEnd]).range([PLOT_LEFT, WIDTH - PLOT_RIGHT]);
  const y = scaleLinear().domain([0, maxValue]).range([HEIGHT - PLOT_BOTTOM, PLOT_TOP]);
  const lastVisibleIndex = outroProgress > 0 ? rows.length - 1 : Math.ceil(dataProgress);
  const points: PlotPoint[] = rows
    .map((row, index) => ({index, value: row[metric]}))
    .filter((point) => point.index >= Math.floor(domainStart) - 1 && point.index <= Math.ceil(domainEnd) + 1)
    .filter((point) => point.index <= lastVisibleIndex);

  const linePath =
    line<PlotPoint>()
      .x((point) => x(point.index))
      .y((point) => y(point.value))
      .curve(curveMonotoneX)(points) ?? '';
  const areaPath =
    area<PlotPoint>()
      .x((point) => x(point.index))
      .y0(HEIGHT - PLOT_BOTTOM)
      .y1((point) => y(point.value))
      .curve(curveMonotoneX)(points) ?? '';

  const cursorX = x(Math.min(dataProgress, rows.length - 1));
  const currentFloor = Math.floor(dataProgress);
  const currentCeil = Math.min(rows.length - 1, currentFloor + 1);
  const fractional = dataProgress - currentFloor;
  const currentValue = interpolate(
    fractional,
    [0, 1],
    [rows[currentFloor][metric], rows[currentCeil][metric]],
  );
  const cursorY = y(currentValue);
  const peakVisible = peak.index >= domainStart && peak.index <= domainEnd;
  const peakMoment = Math.max(0, 1 - Math.abs(dataProgress - peak.index) / 1.5);
  const peakOpacity = outroProgress > 0.55 ? outroProgress : peakMoment;
  const pulse = 11 + 5 * Math.sin((frame / 60) * Math.PI * 2);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => maxValue * ratio);
  const gradientId = `${metric}-gradient`;
  const glowId = `${metric}-glow`;

  return (
    <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={fillArea ? 0.48 : 0.16} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id={`${metric}-clip`}>
          <rect x={PLOT_LEFT} y={0} width={Math.max(0, cursorX - PLOT_LEFT + 3)} height={HEIGHT} />
        </clipPath>
      </defs>

      <text x={0} y={24} fill={color} fontFamily={fontFamily} fontSize={22} fontWeight={600}>
        {label}
        <tspan fill={theme.muted} fontSize={16} fontWeight={400}> {unit}</tspan>
      </text>

      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PLOT_LEFT}
            x2={WIDTH - PLOT_RIGHT}
            y1={y(tick)}
            y2={y(tick)}
            stroke={theme.grid}
            strokeWidth={1}
            strokeDasharray="4 8"
          />
          <text
            x={PLOT_LEFT - 18}
            y={y(tick) + 6}
            fill={theme.muted}
            fontFamily={fontFamily}
            fontSize={15}
            textAnchor="end"
          >
            {formatTick(tick)}
          </text>
        </g>
      ))}

      <g clipPath={`url(#${metric}-clip)`}>
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
          filter={`url(#${glowId})`}
        />
      </g>

      <line
        x1={cursorX}
        x2={cursorX}
        y1={PLOT_TOP - 8}
        y2={HEIGHT - PLOT_BOTTOM}
        stroke={color}
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />
      <circle cx={cursorX} cy={cursorY} r={pulse} fill={color} opacity={0.11} />
      <circle cx={cursorX} cy={cursorY} r={6} fill={theme.background} stroke={color} strokeWidth={3} />

      {peakVisible ? (
        <g opacity={peakOpacity}>
          <circle cx={x(peak.index)} cy={y(peak.value)} r={7} fill={theme.background} stroke={color} strokeWidth={3} />
          <line
            x1={x(peak.index)}
            x2={x(peak.index)}
            y1={Math.max(20, y(peak.value) - 44)}
            y2={y(peak.value) - 10}
            stroke={color}
            strokeWidth={1.5}
          />
          <text
            x={x(peak.index)}
            y={Math.max(16, y(peak.value) - 52)}
            fill={color}
            fontFamily={fontFamily}
            fontSize={17}
            fontWeight={600}
            textAnchor="middle"
          >
            峰值 {formatTick(peak.value)}
          </text>
        </g>
      ) : null}
    </svg>
  );
};
